import type { NavigationSection } from "./navigation.data";
import Link from "next/link";
import Image from "next/image";
import MegaMenuFooter from "./MegaMenuFooter";


function Badge({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[#FCEA981F] text-yellow text-[9px] leading-none px-1.5 py-1 rounded-sm font-mono uppercase tracking-widest shrink-0">
      {children}
    </div>
  );
}

export default function ResourcesMegaMenu({
  clickSubmenu,
  sections,
  footerLabel,
  footerCta,
}: {
  clickSubmenu: (link: string, external?: boolean) => void;
  sections: NavigationSection[];
  footerLabel?: string;
  footerCta?: { title?: string; link?: string };
}) {
  const items = sections?.flatMap((section) => section?.links ?? []) ?? [];
  const groups = sections?.flatMap((section) => section?.groups ?? []) ?? [];
  const authoredPromo = sections?.map((section) => section?.promo).find(Boolean);
  const promo = authoredPromo?.image && authoredPromo?.title && authoredPromo?.link
    ? {
        title: authoredPromo.title,
        subtitle: authoredPromo.subtitle,
        image: authoredPromo.image,
        link: authoredPromo.link,
      }
    : undefined;

  return (
    <MegaMenuFooter clickSubmenu={clickSubmenu} footerLabel={footerLabel} footerCta={footerCta}>
      <div className="flex xl:h-full xl:flex-row flex-col gap-2">
        {/* Items, two columns — no category rail */}
        <div className="flex flex-col gap-6 xl:w-[440px] shrink-0 xl:pt-1">
          {items.length > 0 && (
            <div className="grid grid-cols-2 gap-x-10 gap-y-6 content-start">
              {items.map((item) => (
                <Link
                  key={item.id}
                  href={item.link}
                  target={item.external ? "_blank" : undefined}
                  onClick={() => clickSubmenu(item.link, item.external)}
                  className="group flex flex-col gap-1"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium leading-tight transition-colors group-hover:text-white/70">
                      {item.title}
                    </span>
                    {item.badge && <Badge>{item.badge}</Badge>}
                  </span>
                  <span className="text-grey text-[12px] font-normal leading-snug">
                    {item.subtitle}
                  </span>
                </Link>
              ))}
            </div>
          )}
          {groups.map((group) => (
            <div key={group.id} className="flex flex-col gap-3">
              {group.title && (
                <span className="text-grey-600 text-[11px] uppercase font-semibold tracking-wider leading-none">
                  {group.title}
                </span>
              )}
              <div className="grid grid-cols-2 gap-x-10 gap-y-6 content-start">
                {group.links?.map((item) => (
                  <Link
                    key={item.id}
                    href={item.link}
                    target={item.external ? "_blank" : undefined}
                    onClick={() => clickSubmenu(item.link, item.external)}
                    className="group flex flex-col gap-1"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium leading-tight transition-colors group-hover:text-white/70">
                        {item.title}
                      </span>
                      {item.badge && <Badge>{item.badge}</Badge>}
                    </span>
                    <span className="text-grey text-[12px] font-normal leading-snug">
                      {item.subtitle}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right: promo card, only shown once image/title/link are provided */}
        {promo && (
          <div className="xl:w-[300px] shrink-0">
            <Link
              href={promo.link}
              onClick={() => clickSubmenu(promo.link)}
              className="group relative flex h-full min-h-[220px] flex-col justify-start overflow-hidden rounded-xl border border-[#616161]/50 transition-shadow duration-200 ease-out hover:shadow-[0_0_0_3px_rgba(97,97,97,0.9)]"
            >
              <Image
                src={promo.image}
                alt={promo.title}
                fill
                sizes="300px"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-linear-to-b from-black/80 via-black/20 to-transparent" />
              <div className="relative flex flex-col gap-1 px-5 pt-5">
                {promo.subtitle && (
                  <span className="text-grey text-[11px] font-semibold uppercase tracking-wider leading-none">
                    {promo.subtitle}
                  </span>
                )}
                <span className="text-white text-[16px] font-normal leading-tight">{promo.title}</span>
              </div>
            </Link>
          </div>
        )}
      </div>
    </MegaMenuFooter>
  );
}
